import { useEffect, useMemo, useRef, useState } from "react";
import { Accordion, Alert, Button, Group, Loader, Table, Text, TextInput } from "@mantine/core";
import { IconArrowBackUp, IconCircleCheck, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";
import type { HoldingRow, IssueRecipient } from "../../api/types";
import { RecipientBadge } from "./IssueForm";
import { fmtQty } from "./qty";

export interface HoldingsTabProps {
  canEdit: boolean;
  /** Crește după fiecare operație — reîncarcă soldurile. */
  reloadKey: number;
  onAction: (mode: "return" | "close", recipient: IssueRecipient) => void;
}

type Group_ = { key: string; recipient: IssueRecipient; rows: HoldingRow[] };

/** „La cine e”: soldul net per destinatar (predat − returnat − închis), grupat. */
export function HoldingsTab({ canEdit, reloadKey, onAction }: HoldingsTabProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<HoldingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    const s = ++seq.current;
    setLoading(true);
    setError(false);
    api.getHoldings()
      .then((r) => { if (s === seq.current) setRows(r.rows); })
      .catch(() => { if (s === seq.current) setError(true); })
      .finally(() => { if (s === seq.current) setLoading(false); });
  }, [reloadKey]);

  const groups = useMemo(() => {
    const m = new Map<string, Group_>();
    for (const r of rows) {
      const key = `${r.recipient.type}:${r.recipient.id}`;
      let g = m.get(key);
      if (!g) {
        g = {
          key,
          recipient: { type: r.recipient.type, id: r.recipient.id, name: r.recipient.name ?? `#${r.recipient.id}` },
          rows: [],
        };
        m.set(key, g);
      }
      g.rows.push(r);
    }
    const needle = q.trim().toLowerCase();
    return [...m.values()]
      .filter((g) => !needle || g.recipient.name.toLowerCase().includes(needle)
        || g.rows.some((r) => (r.name ?? "").toLowerCase().includes(needle)))
      .sort((a, b) =>
        a.recipient.type === b.recipient.type
          ? a.recipient.name.localeCompare(b.recipient.name)
          : a.recipient.type === "technician" ? -1 : 1);
  }, [rows, q]);

  if (loading && rows.length === 0) return <Group justify="center" p="md"><Loader size="sm" /></Group>;
  if (error) return <Alert color="red">{t("freeIssues.loadFailed")}</Alert>;

  return (
    <>
      <TextInput
        mb="sm"
        leftSection={<IconSearch size={16} />}
        placeholder={t("freeIssues.holdingsSearch")}
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        w={{ base: "100%", sm: 320 }}
      />
      {groups.length === 0 ? (
        <Text c="dimmed">{t("freeIssues.holdingsEmpty")}</Text>
      ) : (
        <Accordion multiple variant="separated">
          {groups.map((g) => (
            <Accordion.Item key={g.key} value={g.key}>
              <Accordion.Control>
                <Group gap="xs" wrap="nowrap" justify="space-between" pr="xs">
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text fw={500} truncate>{g.recipient.name}</Text>
                    <RecipientBadge type={g.recipient.type} />
                  </Group>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                    {t("freeIssues.linesCount", { count: g.rows.length })}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Table withTableBorder striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("freeIssues.colItem")}</Table.Th>
                      <Table.Th ta="right">{t("freeIssues.colQty")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {g.rows.map((r) => (
                      <Table.Tr key={r.stock_item_id}>
                        <Table.Td>{r.name ?? `#${r.stock_item_id}`}</Table.Td>
                        <Table.Td ta="right" style={{ whiteSpace: "nowrap" }}>{fmtQty(r.quantity)} {r.unit ?? ""}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {canEdit && (
                  <Group mt="sm" gap="xs" justify="flex-end">
                    <Button
                      size="xs"
                      variant="light"
                      color="teal"
                      leftSection={<IconArrowBackUp size={14} />}
                      onClick={() => onAction("return", g.recipient)}
                    >
                      {t("freeIssues.actionReturn")}
                    </Button>
                    {g.recipient.type === "technician" && (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconCircleCheck size={14} />}
                        onClick={() => onAction("close", g.recipient)}
                      >
                        {t("freeIssues.actionClose")}
                      </Button>
                    )}
                  </Group>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </>
  );
}
